import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Users,
  ShoppingCart,
  Package,
  Phone,
  Mail,
  MapPin,
  CheckCircle,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Star,
  Truck,
  Shield,
  Award,
  UserCog,
  ShoppingBag
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default placeholder images for products without images
const DEFAULT_IMAGES = {
  white: "https://images.unsplash.com/photo-1770843093640-c44ae557928b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxBZnJpY2FuJTIwY2hpbGRyZW4lMjBjbGFzc3Jvb20lMjBzY2hvb2wlMjBlZHVjYXRpb258ZW58MHx8fHwxNzcyMTI2MTczfDA&ixlib=rb-4.1.0&q=85",
  colored: "https://images.unsplash.com/photo-1632215861513-130b66fe97f4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwzfHxBZnJpY2FuJTIwY2hpbGRyZW4lMjBjbGFzc3Jvb20lMjBzY2hvb2wlMjBlZHVjYXRpb258ZW58MHx8fHwxNzcyMTI2MTczfDA&ixlib=rb-4.1.0&q=85",
  bulk: "/bulk-orders.jpg",
  hero: "/hero-classroom.jpg"
};

// Shown until staff add slides under Website → Homepage Slider.
const DEFAULT_SLIDE = {
  id: 'default',
  image_url: DEFAULT_IMAGES.hero,
  title: 'Premium Quality Chalk for Education',
  subtitle: "Tanzania's trusted chalk manufacturer. Supplying schools, institutions, and businesses with high-quality dustless chalk at competitive prices.",
  cta_text: '',
  cta_link: ''
};

const productImage = (product) =>
  product.image_url || (product.category === 'colored' ? DEFAULT_IMAGES.colored : DEFAULT_IMAGES.white);

export default function HomePage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [slides, setSlides] = useState([DEFAULT_SLIDE]);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    fetchProducts();
    fetchSlides();
  }, []);

  const fetchSlides = async () => {
    try {
      const response = await axios.get(`${API}/homepage/slides/public`);
      if (Array.isArray(response.data) && response.data.length > 0) {
        setSlides(response.data);
        setCurrent(0);
      }
    } catch (error) {
      // Keep the built-in slide when the API is unreachable.
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products/public`);
      // A product reaches the welcome page only when it is both active and
      // toggled on for the homepage, so staff can hide any of them.
      setProducts((response.data || []).filter(p => p.is_active && p.featured).slice(0, 6));
    } catch (error) {
      setProducts([]);
    }
  };

  const goTo = useCallback((index) => {
    setCurrent((index + slides.length) % slides.length);
  }, [slides.length]);

  useEffect(() => {
    if (slides.length < 2) return undefined;
    const timer = setInterval(() => setCurrent(c => (c + 1) % slides.length), 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const slide = slides[Math.min(current, slides.length - 1)] || DEFAULT_SLIDE;

  const features = [
    { icon: Truck, title: "Fast Delivery", description: "Quick delivery across Tanzania" },
    { icon: Shield, title: "Quality Assured", description: "Premium dustless chalk products" },
    { icon: Award, title: "Best Prices", description: "Competitive wholesale & retail pricing" },
    { icon: Star, title: "Trusted Brand", description: "Serving schools since establishment" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <img src="/himan-logo.png" alt="Himan Logo" className="h-12 w-12 object-contain" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Himan Investment & Company</h1>
                <p className="text-xs text-gray-500">Quality Chalk Manufacturer</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Button 
                variant="outline" 
                onClick={() => navigate('/login')}
                data-testid="staff-login-nav-btn"
                className="border-[#8FC043] text-[#8FC043] hover:bg-[#8FC043] hover:text-white"
              >
                Staff Login
              </Button>
              <Button 
                onClick={() => navigate('/customer/login')}
                data-testid="customer-login-nav-btn"
                className="bg-[#EA5829] hover:bg-[#d14d23] text-white"
              >
                Customer Portal
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#8FC043] to-[#6fa030] text-white py-20 lg:py-28">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight" data-testid="hero-title">
                {slide.title || DEFAULT_SLIDE.title}
              </h1>
              <p className="text-lg sm:text-xl text-green-100 mb-8 max-w-xl">
                {slide.subtitle || DEFAULT_SLIDE.subtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button
                  size="lg"
                  onClick={() => navigate(slide.cta_link || '/customer/register')}
                  data-testid="hero-register-btn"
                  className="bg-white text-[#8FC043] hover:bg-gray-100 font-semibold text-lg px-8"
                >
                  {slide.cta_text || 'Start Ordering'} <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate('/customer/login')}
                  data-testid="hero-login-btn"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#8FC043] font-semibold text-lg px-8"
                >
                  Customer Login
                </Button>
              </div>
            </div>

            {/* Hero slider — images managed under Website → Homepage Slider */}
            <div className="relative" data-testid="hero-slider">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white/20 aspect-[4/3] bg-white/10">
                {slides.map((s, index) => (
                  <img
                    key={s.id || index}
                    src={s.image_url}
                    alt={s.title || 'Himan Investment'}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                      index === current ? 'opacity-100' : 'opacity-0'
                    }`}
                  />
                ))}
              </div>

              {slides.length > 1 && (
                <>
                  <button
                    type="button"
                    aria-label="Previous slide"
                    onClick={() => goTo(current - 1)}
                    data-testid="slider-prev"
                    className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg transition-colors"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    aria-label="Next slide"
                    onClick={() => goTo(current + 1)}
                    data-testid="slider-next"
                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg transition-colors"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                  <div className="flex justify-center gap-2 mt-4">
                    {slides.map((s, index) => (
                      <button
                        key={s.id || index}
                        type="button"
                        aria-label={`Go to slide ${index + 1}`}
                        onClick={() => goTo(index)}
                        className={`h-2.5 rounded-full transition-all ${
                          index === current ? 'bg-white w-8' : 'bg-white/50 hover:bg-white/70 w-2.5'
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="text-center p-6 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow"
                data-testid={`feature-card-${index}`}
              >
                <div className="inline-flex items-center justify-center w-14 h-14 bg-[#EA5829]/10 rounded-full mb-4">
                  <feature.icon className="h-7 w-7 text-[#EA5829]" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision, Mission & Objectives Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Our Purpose</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Committed to transforming education through sustainable, innovative products
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Vision */}
            <Card className="border-l-4 border-l-[#8FC043] hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="bg-[#8FC043]/10 p-3 rounded-full">
                    <Star className="h-6 w-6 text-[#8FC043]" />
                  </div>
                  <CardTitle className="text-2xl text-[#8FC043]">Our Vision</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  To be the global leader in sustainable, dustless writing tools, setting the standard for eco-conscious educational products. We envision a future where every classroom is clean, safe, and inspiring.
                </p>
              </CardContent>
            </Card>

            {/* Mission */}
            <Card className="border-l-4 border-l-[#EA5829] hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="bg-[#EA5829]/10 p-3 rounded-full">
                    <Award className="h-6 w-6 text-[#EA5829]" />
                  </div>
                  <CardTitle className="text-2xl text-[#EA5829]">Our Mission</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  To deliver high-quality, eco-friendly, dust-free chalk that transforms learning environments. We prioritize safety and sustainability, aiming to support educators and students with innovative writing solutions.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Value Proposition */}
          <Card className="mb-8 bg-gradient-to-r from-[#8FC043]/5 to-[#EA5829]/5 border-0 shadow-lg">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="bg-white p-4 rounded-full shadow-md">
                  <Shield className="h-8 w-8 text-[#8FC043]" />
                </div>
              </div>
              <CardTitle className="text-2xl">Our Value Proposition</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 text-center text-lg leading-relaxed max-w-4xl mx-auto">
                Himan Investment & Company promises to deliver high-quality, dustless chalks that enhance the health, efficiency, and overall experience of our customers in educational and professional environments.
              </p>
            </CardContent>
          </Card>

          {/* Objectives */}
          <Card className="border-t-4 border-t-[#8FC043] hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-[#8FC043]/10 p-3 rounded-full">
                  <CheckCircle className="h-6 w-6 text-[#8FC043]" />
                </div>
                <CardTitle className="text-2xl">Our Objectives</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed">
                Our fundamental objective is to improve the efficiency and quality of our fast, dustless school chalk production and supply chain management process as an exponential function of our market expansion and business growth strategy in Tanzania and the broader East African regional market. Our commitment to customer satisfaction is ongoing, and through continuous investment in our people and business, we are confident in maintaining and improving our successful business practice.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Products Section — hidden entirely when nothing is toggled on for the homepage */}
      <section className={`py-20 bg-gray-50 ${products.length === 0 ? 'hidden' : ''}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Our Products</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              High-quality chalk products for education and professional use
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <Card
                key={product.id || index}
                className="overflow-hidden hover:shadow-xl transition-shadow border-0 shadow-md flex flex-col"
                data-testid={`product-card-${index}`}
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={productImage(product)}
                    alt={product.name}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <CardHeader>
                  <CardTitle className="text-xl">{product.name}</CardTitle>
                  <CardDescription>{product.description}</CardDescription>
                </CardHeader>
                <CardContent className="mt-auto">
                  <div className="mb-4 space-y-1">
                    <p className="text-sm">
                      <span className="font-semibold">Retail:</span>{' '}
                      {product.retail_price ? `Tsh ${product.retail_price.toLocaleString()}` : 'Contact us'}
                    </p>
                    <p className="text-sm text-[#8FC043]">
                      <span className="font-semibold">Wholesale:</span>{' '}
                      {product.wholesale_price ? `From Tsh ${product.wholesale_price.toLocaleString()}` : 'Custom pricing'}
                    </p>
                  </div>
                  <Button
                    className="w-full bg-[#8FC043] hover:bg-[#7aa936]"
                    onClick={() => navigate('/customer/products')}
                  >
                    Order Now
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-12 bg-white rounded-2xl shadow-md p-8 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Bulk Orders for Schools & Institutions</h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Special wholesale pricing and VIP rates for schools, colleges and resellers ordering in volume.
            </p>
            <Button
              className="bg-[#EA5829] hover:bg-[#d14d23] text-white px-8"
              onClick={() => navigate('/customer/register')}
            >
              Request Wholesale Pricing <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">Why Choose Himan Investment?</h2>
              <p className="text-gray-400 text-lg mb-8">
                We are committed to providing the highest quality chalk products to educational institutions across Tanzania.
              </p>
              <ul className="space-y-4">
                {[
                  "Premium dustless chalk - safe for health",
                  "Competitive wholesale pricing for bulk orders",
                  "Fast and reliable delivery across Tanzania",
                  "VIP customer programs with special pricing",
                  "Dedicated customer support"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-[#8FC043] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gray-800 p-8 rounded-2xl">
              <h3 className="text-2xl font-bold mb-6 text-center">Get Started Today</h3>
              <div className="space-y-4">
                <Card className="bg-white/10 border-0 cursor-pointer hover:bg-white/20 transition-colors" onClick={() => navigate('/customer/register')}>
                  <CardContent className="p-6 flex items-center gap-4">
                    <div className="bg-[#8FC043] p-3 rounded-full">
                      <ShoppingCart className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">New Customer?</h4>
                      <p className="text-sm text-gray-400">Register and start ordering today</p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-gray-400 ml-auto" />
                  </CardContent>
                </Card>
                <Card className="bg-white/10 border-0 cursor-pointer hover:bg-white/20 transition-colors" onClick={() => navigate('/customer/login')}>
                  <CardContent className="p-6 flex items-center gap-4">
                    <div className="bg-[#EA5829] p-3 rounded-full">
                      <Users className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">Existing Customer?</h4>
                      <p className="text-sm text-gray-400">Login to manage your orders</p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-gray-400 ml-auto" />
                  </CardContent>
                </Card>
                <Card className="bg-white/10 border-0 cursor-pointer hover:bg-white/20 transition-colors" onClick={() => navigate('/login')}>
                  <CardContent className="p-6 flex items-center gap-4">
                    <div className="bg-gray-700 p-3 rounded-full">
                      <Package className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">Staff Member?</h4>
                      <p className="text-sm text-gray-400">Access the staff portal</p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-gray-400 ml-auto" />
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20" id="contact">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Contact Us</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Have questions? We're here to help. Reach out to our team.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="text-center p-6 hover:shadow-lg transition-shadow" data-testid="contact-phone-card">
              <CardContent className="pt-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#8FC043]/10 rounded-full mb-4">
                  <Phone className="h-8 w-8 text-[#8FC043]" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Phone</h3>
                <p className="text-gray-600">+255 767 408 555</p>
                <p className="text-sm text-gray-500 mt-1">Mon-Sat, 8AM-6PM</p>
              </CardContent>
            </Card>
            <Card className="text-center p-6 hover:shadow-lg transition-shadow" data-testid="contact-email-card">
              <CardContent className="pt-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#EA5829]/10 rounded-full mb-4">
                  <Mail className="h-8 w-8 text-[#EA5829]" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Email</h3>
                <p className="text-gray-600">himaninvestmentcompany@gmail.com</p>
                <p className="text-sm text-gray-500 mt-1">We reply within 24 hours</p>
              </CardContent>
            </Card>
            <Card className="text-center p-6 hover:shadow-lg transition-shadow" data-testid="contact-address-card">
              <CardContent className="pt-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                  <MapPin className="h-8 w-8 text-gray-700" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Address</h3>
                <p className="text-gray-600">Arusha, Tanzania</p>
                <p className="text-sm text-gray-500 mt-1">Visit our office</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <img src="/himan-logo.png" alt="Himan Logo" className="h-12 w-12 object-contain rounded-lg" />
                <span className="text-xl font-bold">Himan Investment & Company</span>
              </div>
              <p className="text-gray-400 mb-4 max-w-md">
                Leading chalk manufacturer in Tanzania, providing quality educational supplies to schools and institutions nationwide.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => navigate('/customer/products')} className="hover:text-white transition-colors">Products</button></li>
                <li><button onClick={() => navigate('/customer/register')} className="hover:text-white transition-colors">Register</button></li>
                <li><button onClick={() => navigate('/customer/login')} className="hover:text-white transition-colors">Customer Login</button></li>
                <li><button onClick={() => navigate('/login')} className="hover:text-white transition-colors">Staff Portal</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Business Hours</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>Monday - Friday: 8AM - 6PM</li>
                <li>Saturday: 9AM - 4PM</li>
                <li>Sunday: Closed</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-500 text-sm">
            <p>&copy; {new Date().getFullYear()} Himan Investment & Company. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
